import { segmentGraphemes } from './textTypography';
import {
  NarrationPaginationResult,
  NarrationSourceMapEntry,
  NarrationTextBlockLike,
  paginateNarrationBlocks
} from './narrationPagination';

export const TEXT_ONLY_CONTINUATION_TEMPLATE = 'text-only' as const;

export interface NarrationFlowLayoutLike {
  requiresSplit: boolean;
}

export interface NarrationContinuationMetadata {
  sourcePageId: string;
  continuationIndex: number;
  continuationCount: number;
}

export interface PlanNarrationPageFlowInput<
  TBlock extends NarrationTextBlockLike,
  TLayout extends NarrationFlowLayoutLike,
  TFirstTemplate extends string,
  TSourceMetadata extends Record<string, unknown>
> {
  sourcePageId: string;
  sourceBlocks: readonly TBlock[];
  firstTemplate: TFirstTemplate;
  sourceMetadata?: Readonly<TSourceMetadata>;
  buildLayout: (
    blocks: readonly TBlock[],
    template: TFirstTemplate | typeof TEXT_ONLY_CONTINUATION_TEMPLATE
  ) => TLayout;
}

export interface PlannedNarrationFlowPage<
  TBlock extends NarrationTextBlockLike,
  TLayout extends NarrationFlowLayoutLike,
  TTemplate extends string,
  TSourceMetadata extends Record<string, unknown>
> extends NarrationContinuationMetadata {
  template: TTemplate | typeof TEXT_ONLY_CONTINUATION_TEMPLATE;
  blocks: TBlock[];
  layout: TLayout;
  metadata: TSourceMetadata & NarrationContinuationMetadata;
}

export interface NarrationPageFlowPlan<
  TBlock extends NarrationTextBlockLike,
  TLayout extends NarrationFlowLayoutLike,
  TTemplate extends string,
  TSourceMetadata extends Record<string, unknown>
> {
  pages: Array<PlannedNarrationFlowPage<
    TBlock,
    TLayout,
    TTemplate,
    TSourceMetadata
  >>;
  sourceMap: NarrationSourceMapEntry[];
}

const firstVisibleGraphemePrefix = <TBlock extends NarrationTextBlockLike>(
  sourceBlocks: readonly TBlock[]
): TBlock | null => {
  for (const source of sourceBlocks) {
    const content = String(source.content ?? '');
    let prefix = '';
    for (const grapheme of segmentGraphemes(content)) {
      prefix += grapheme;
      if (/\S/u.test(prefix)) return { ...source, content: prefix };
    }
  }
  return null;
};

const shiftSourceMapPages = (
  sourceMap: NarrationSourceMapEntry[],
  pageOffset: number
): NarrationSourceMapEntry[] => sourceMap.map(entry => ({
  sourceIndex: entry.sourceIndex,
  outputs: entry.outputs.map(output => ({
    ...output,
    pageIndex: output.pageIndex + pageOffset
  }))
}));

const isUnfittableGraphemeError = (error: unknown) => (
  error instanceof Error
  && /cannot fit even one grapheme on an empty page/i.test(error.message)
);

/**
 * Plans a source page and any text-only continuation pages without mutating the
 * source blocks or page metadata. The supplied layout builder remains the
 * authority on fit, so editor and export code can share the same measurements.
 */
export const planNarrationPageFlow = <
  TBlock extends NarrationTextBlockLike,
  TLayout extends NarrationFlowLayoutLike,
  TFirstTemplate extends string,
  TSourceMetadata extends Record<string, unknown> = Record<string, never>
>({
  sourcePageId,
  sourceBlocks,
  firstTemplate,
  sourceMetadata,
  buildLayout
}: PlanNarrationPageFlowInput<
  TBlock,
  TLayout,
  TFirstTemplate,
  TSourceMetadata
>): NarrationPageFlowPlan<TBlock, TLayout, TFirstTemplate, TSourceMetadata> => {
  const minimumFirstFragment = firstVisibleGraphemePrefix(sourceBlocks);
  const preserveEmptyFirstPage = Boolean(
    minimumFirstFragment
    && firstTemplate !== TEXT_ONLY_CONTINUATION_TEMPLATE
    && buildLayout([minimumFirstFragment], firstTemplate).requiresSplit
  );
  const pageOffset = preserveEmptyFirstPage ? 1 : 0;

  let pagination: NarrationPaginationResult<TBlock>;
  try {
    pagination = paginateNarrationBlocks(
      sourceBlocks,
      (blocks, paginationPageIndex) => {
        const flowPageIndex = paginationPageIndex + pageOffset;
        const template = flowPageIndex === 0
          ? firstTemplate
          : TEXT_ONLY_CONTINUATION_TEMPLATE;
        return !buildLayout(blocks, template).requiresSplit;
      }
    );
  } catch (error) {
    if (isUnfittableGraphemeError(error)) {
      throw new Error(
        'Narration cannot fit even one grapheme on a text-only continuation page.'
      );
    }
    throw error;
  }

  let paginatedBlocks = pagination.pages;
  let sourceMap = pagination.sourceMap;
  if (preserveEmptyFirstPage) {
    paginatedBlocks = [[], ...paginatedBlocks];
    sourceMap = shiftSourceMapPages(sourceMap, 1);
  } else if (paginatedBlocks.length === 0) {
    // An original page still exists when it contains no visible text.
    paginatedBlocks = [[]];
  }

  const continuationCount = paginatedBlocks.length;
  const pages = paginatedBlocks.map((blocks, continuationIndex) => {
    const template = continuationIndex === 0
      ? firstTemplate
      : TEXT_ONLY_CONTINUATION_TEMPLATE;
    const continuationMetadata: NarrationContinuationMetadata = {
      sourcePageId,
      continuationIndex,
      continuationCount
    };
    const metadata = {
      ...(sourceMetadata || {} as TSourceMetadata),
      ...continuationMetadata
    } as TSourceMetadata & NarrationContinuationMetadata;

    return {
      ...continuationMetadata,
      template,
      blocks,
      layout: buildLayout(blocks, template),
      metadata
    };
  });

  return { pages, sourceMap };
};
